import { InputProps } from "@/components/ui/input"
import { forwardRef, useState } from "react"
import { useAddressAutocomplete } from "../../hooks/useAddressAutocomplete"
import { GeocodeResult } from "use-places-autocomplete"
import { MapPin } from "lucide-react"
import React from "react"
import { Small } from "@/components/ui/typography"

export interface AddressAutocompleteData {
	formattedAddress: string
	streetNumber: string
	street: string
	city: string
	state: string
	country: string
	postalCode: string
	lat: number
	lng: number
}

type GoogleAutocompleteSuggestion = {
	main: string
	secondary: string
	place_id: string
	description: string
}
type GoogleAddressAutocompleteProps = {
	suggestions: GoogleAutocompleteSuggestion[]
	onSuggestionSelect: (description: string) => void
}

export const GoogleAddressAutocomplete = forwardRef<
	HTMLDivElement,
	React.PropsWithChildren<GoogleAddressAutocompleteProps>
>(({ suggestions, onSuggestionSelect, ...props }, ref) => {
	return (
		<div className="relative w-full" ref={ref}>
			<div className="relative">{props.children}</div>
			{suggestions.length > 0 && (
				<div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-lg border bg-white shadow-md">
					<div className="py-1 px-1.5">
						{suggestions.length === 0 ? (
							<div className="px-2 py-3 text-sm text-muted-foreground">
								No locations found.
							</div>
						) : (
							suggestions.map((suggestion) => (
								<button
									key={suggestion.place_id}
									className="w-full rounded-lg px-2 py-1.5 text-left hover:bg-accent focus:bg-accent focus:outline-none"
									onClick={() =>
										onSuggestionSelect(suggestion.description)
									}
								>
									<div className="flex items-center">
										<MapPin className="size-4 mr-2" />
										<Small className="font-normal text-accent-foreground">
											{suggestion.main} {suggestion.secondary}
										</Small>
									</div>
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	)
})

type AutocompleteSuggestion = {
	displayName: string
	placeId: string
	lat: number
	lon: number
}
type AddressAutocompleteProps = {
	suggestions: AutocompleteSuggestion[]
	onSuggestionSelect: (suggestion: AutocompleteSuggestion) => void
}

export const AddressAutocomplete = forwardRef<
	HTMLDivElement,
	React.PropsWithChildren<AddressAutocompleteProps>
>(({ suggestions, onSuggestionSelect, ...props }, ref) => {
	return (
		<div className="relative w-full" ref={ref}>
			<div className="relative">{props.children}</div>
			{suggestions.length > 0 && (
				<div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-lg border bg-white shadow-md">
					<div className="py-1 px-1.5">
						{suggestions.length === 0 ? (
							<div className="px-2 py-3 text-sm text-muted-foreground">
								No locations found.
							</div>
						) : (
							suggestions.map((suggestion) => (
								<button
									key={suggestion.placeId}
									className="w-full rounded-lg px-2 py-1.5 text-left hover:bg-accent focus:bg-accent focus:outline-none"
									onClick={() => onSuggestionSelect(suggestion)}
								>
									<div className="flex items-center">
										<MapPin className="size-4 mr-2" />
										<Small className="font-normal text-accent-foreground">
											{suggestion.displayName}
										</Small>
									</div>
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	)
})
type AddressAutocompleteContainerProps = {
	onChangeAddress: (address: GeocodeResult) => void
	input: React.ReactElement<InputProps>
	inputProps?: InputProps
}

export const AddressAutocompleteContainer: React.FC<
	AddressAutocompleteContainerProps
> = ({ onChangeAddress, input, ...props }) => {
	const { value, suggestions, setSuggest, findByAddress, ref } = useAddressAutocomplete(
		{
			defaultValue: props.inputProps?.value?.toString(),
			onChangeAddress,
		}
	)

	return (
		<GoogleAddressAutocomplete
			suggestions={suggestions.map((suggestion) => ({
				main: suggestion.structured_formatting.main_text,
				secondary: suggestion.structured_formatting.secondary_text,
				place_id: suggestion.place_id,
				description: suggestion.description,
			}))}
			onSuggestionSelect={findByAddress}
			ref={ref}
		>
			{React.cloneElement(input, {
				autoComplete: "off",
				...props.inputProps,
				value,
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
					setSuggest(e.target.value)
					props.inputProps?.onChange?.(e)
				},
			})}
		</GoogleAddressAutocomplete>
	)
}
